import React, { useCallback, useEffect, useState } from 'react';
import imgPlaceholder from '~/assets/img/ImgPlaceholder.png';
import { useCart } from '~/context/OrderingSystem/Cart';
import formatValue from '~/utils/formatValue';
import { startingPrice } from '~/utils/functions';
import { TypeBadge } from '~/components/TypeBadge';
import { useAuth } from '~/context/AuthContext';

import { Tooltip } from 'reactstrap';
import {
  Container,
  CountBadge,
  ProductCard,
  ProductImg,
  ProductInfo,
  ProductsNotFound,
} from './styles';

export const BalconyProducts = ({
  products,
  setAdded,
  toggleComplementArea,
  hasCodebar,
  setHasCodebar,
  scannedBarcode,
  is_drive_thru = false,
  areaRef,
}) => {
  const { addToCart, countProducts, productsHasCounted } = useCart();
  const { user } = useAuth();
  const productsFiltered = products?.filter((product) => product.available);

  function extractScaleCode(barcode) {
    if (!barcode || barcode.length < 7) return null;

    const codeWithoutIdentifier = barcode.slice(0, -1);

    const scaleCode = codeWithoutIdentifier.slice(-5);

    return scaleCode;
  }

  const handleSelectProduct = useCallback(
    (product, scannedBarcodeInner) => {
      if (
        (product?.complement_categories.length > 0 || product?.use_weight) &&
        !hasCodebar
      ) {
        toggleComplementArea({
          ...product,
          price: product?.price_promotion || product?.price,
        });
      } else if (
        product?.use_weight &&
        user.ean_scale_mode !== 'none' &&
        hasCodebar
      ) {
        const codeSliced = extractScaleCode(scannedBarcodeInner);

        const priceFormatted = parseInt(codeSliced, 10) / 100;
        const weightFormatted = (priceFormatted / product.price).toFixed(3);

        if (product.ean_codes && product.ean_codes.length > 0) {
          if (product.ean_codes[0].startsWith('$')) {
            countProducts({ id: product.id, amount: 1 });
            const productFormatted = {
              ...product,
              price: product.price_promotion || product.price,
            };

            addToCart({
              product: productFormatted,
              amount: 1,
              observation: '',
              complementsCategory: [],
              isProductWeight: product?.use_weight,
              inputProductWeight: weightFormatted,
              inputProductPrice: parseInt(codeSliced, 10) / 100,
            });
          }

          if (product.ean_codes[0].startsWith('#')) {
            countProducts({ id: product.id, amount: 1 });
            const productFormatted = {
              ...product,
              price: product.price_promotion || product.price,
            };

            addToCart({
              product: productFormatted,
              amount: 1,
              observation: '',
              complementsCategory: [],
              isProductWeight: product?.use_weight,
              inputProductWeight: parseInt(codeSliced) / 1000,
            });
          }
        }
      } else {
        countProducts({ id: product.id, amount: 1 });
        const productFormatted = {
          ...product,
          price: product.price_promotion || product.price,
        };

        if (setAdded) {
          setAdded((state) => {
            const exists = state.find(
              (s) => s.product.id === productFormatted.id
            );
            if (exists) {
              return state.map((s) => {
                if (s.product.id === productFormatted.id) {
                  return { ...s, amount: s.amount + 1 };
                }
                return s;
              });
            }
            return [
              ...state,
              {
                product: productFormatted,
                amount: 1,
                observation: '',
                complementsCategory: [],
                isProductWeight: product?.use_weight,
                created_at: new Date().toDateString(),
              },
            ];
          });
        }
        addToCart({
          product: productFormatted,
          amount: 1,
          observation: '',
          complementsCategory: [],
          isProductWeight: product?.use_weight,
        });
      }
    },
    [addToCart, countProducts, setAdded, toggleComplementArea, hasCodebar]
  );

  useEffect(() => {
    if (hasCodebar && productsFiltered && productsFiltered.length > 0) {
      handleSelectProduct(productsFiltered[0], scannedBarcode);
      setHasCodebar(false);
    }
  }, [
    hasCodebar,
    productsFiltered,
    setHasCodebar,
    handleSelectProduct,
    scannedBarcode,
  ]);

  // tooltip
  const [productTooltip, setProductTooltip] = useState([]);

  function toggleTip(id) {
    if (productTooltip.includes(id)) {
      setProductTooltip([]);

      return;
    }
    setProductTooltip([id]);
  }

  return (
    <Container>
      {productsFiltered?.length > 0 ? (
        productsFiltered?.map((product) => {
          const isNotPromo = !product.price_promotion || product.price_promotion === 0;
          const is_combo = product.is_combo;

          let base_price = 0
          const { price, combo_price_promo, price_promotion, combo_price } = product;

          if (isNotPromo) {
            base_price = is_combo
              ? Number(combo_price ?? 0)
              : Number(price);
          } else {
            base_price = is_combo
              ? Number(combo_price_promo ?? price_promotion ?? price)
              : Number(price_promotion ?? price);
          }


          const productStartingPrice = product.has_starting_price
            ? startingPrice(product.complement_categories)
            : 0;

          const badgeCount = productsHasCounted
            ? productsHasCounted.find((pr) => pr.id === product.id)?.amount
            : 0;

          return (
            <ProductCard
              key={product.id}
              onClick={() => handleSelectProduct(product)}
              id={`product-${product.id}`}
            >
              <Tooltip
                key={product.id}
                isOpen={productTooltip.includes(product.id)}
                toggle={() => toggleTip(product.id)}
                target={`product-${product.id}`}
              >
                {product.ean_codes?.length === 1 ? (
                  <p style={{ fontSize: 14 }}>
                    {product.ean_codes[0].slice(1)} - {product.name}
                  </p>
                ) : (
                  <p>{product.name}</p>
                )}
              </Tooltip>
              {product.is_combo && <TypeBadge type="COMBO" />}

              {product.use_weight && <TypeBadge type="PESO" />}

              <ProductImg
                src={product.image?.url ? product.image.url : imgPlaceholder}
              />
              <ProductInfo>
                <h5>{product.name}</h5>
                <span>
                  {formatValue(parseFloat(base_price) + productStartingPrice)}
                </span>
              </ProductInfo>

              {badgeCount > 0 && <CountBadge>{badgeCount}</CountBadge>}
            </ProductCard>
          );
        })
      ) : (
        <ProductsNotFound>
          Nenhum produto encontrado.{' '}
          {is_drive_thru &&
            'Verifique as categorias disponíveis no Drive-Thru.'}{' '}
        </ProductsNotFound>
      )}
    </Container>
  );
};
